import * as z from "zod";
import { cursorPagination, ean, version } from "../index";
import { erpProductTypes } from "../reference/enums";
import { linesheetStatuses, productStatuses } from "./enums";
import { itemCode, productFields, tomorrowColourCode } from "./fields";

/** Products by style code; `season` keeps those in a linesheet of that season. */
export const listProductsInput = z.object({
  brand: z.uuid().optional(),
  season: z.string().max(20).optional(),
  status: z.enum(productStatuses).optional(),
  /** Part of the style code or name, without case and accents; codes by letters and digits (ADR-117). */
  q: z.string().trim().min(1).max(100).optional(),
  ...cursorPagination({ defaultLimit: 50, maxLimit: 200 }),
});
export type ListProductsInput = z.input<typeof listProductsInput>;

export const getProductInput = z.object({ productId: z.uuid() });
export type GetProductInput = z.input<typeof getProductInput>;

/** Tomorrow's codes of a product (ADR-188): only Tomorrow writes them, in a product and in a line. */
export const tomorrowCodeFields = ["itemCode", "tomorrowColourCode"] as const;

/**
 * Tomorrow's codes of a product (ADR-188), from Backstage only: an absent field stays as it is, `null` empties it.
 * Once its SKUs have codes they can only be the ones those codes start with (`codes_locked`).
 */
export const updateProductCodesInput = z.object({
  productId: z.uuid(),
  version,
  itemCode: itemCode.nullable().optional(),
  tomorrowColourCode: tomorrowColourCode.nullable().optional(),
});
export type UpdateProductCodesInput = z.input<typeof updateProductCodesInput>;
export const updateProductCodesBody = updateProductCodesInput.omit({ productId: true });

export const getSkuByEanInput = z.object({
  ean,
});
export type GetSkuByEanInput = z.input<typeof getSkuByEanInput>;

export const productListItemDto = z
  .object({
    productId: z.uuid(),
    brandOrgId: z.uuid().optional(),
    brand: z.string().optional(),
    itemCode: z.string().nullable().optional(),
    styleCode: z.string().optional(),
    styleName: z.string().nullable().optional(),
    colour: z.string().nullable().optional(),
    status: z.enum(productStatuses).optional(),
    firstSeason: z.string().nullable().optional(),
  })
  .meta({ id: "ProductListItem" });

/** A product (style-colour) with its SKUs and the linesheets it is in. */
export const productDto = z
  .object({
    productId: z.uuid(),
    brandOrgId: z.uuid().optional(),
    brand: z.string().optional(),
    itemCode: z.string().nullable().optional(),
    tomorrowColourCode: z.string().nullable().optional(),
    status: z.enum(productStatuses).optional(),
    ...productFields,
    firstSeason: z.string().nullable().optional(),
    /**
     * The type of the style in the file to the ERP, over the one of its subcategory or category (V-16, ADR-257); null,
     * theirs. Tomorrow's only.
     */
    erpProductType: z.enum(erpProductTypes).nullable().optional(),
    version: z.number().int(),
    skus: z.array(
      z.object({
        skuId: z.uuid(),
        skuCode: z.string().nullable(),
        ean: z.string().nullable(),
        size: z.string(),
        status: z.enum(productStatuses),
      }),
    ),
    linesheets: z.array(
      z.object({ linesheetId: z.uuid(), name: z.string(), season: z.string(), status: z.enum(linesheetStatuses) }),
    ),
  })
  .meta({ id: "Product" });

/** An SKU with its product and customs attributes. */
export const skuDto = z
  .object({
    skuId: z.uuid(),
    skuCode: z.string().nullable().optional(),
    ean: z.string().nullable().optional(),
    size: z.string().optional(),
    productId: z.uuid().optional(),
    itemCode: z.string().nullable().optional(),
    styleName: z.string().nullable().optional(),
    colour: z.string().nullable().optional(),
    hsCode: z.string().nullable().optional(),
    commodityCode: z.string().nullable().optional(),
    manufacturerId: z.uuid().nullable().optional(),
    composition: z.string().nullable().optional(),
  })
  .meta({ id: "Sku" });
